import classes from "./classes.module.scss";
import Typography from "components/elements/Typography";
import ForumPostResponseResource from "common/resources/Project/Config/Forum/ForumPostResponseResource";
import { translateISO369 } from "utils/I18nText";
import PostAuthorInfo from "./PostAuthorInfo";
import { container } from "tsyringe";
import ForumPostService from "services/projects/ForumPostService";
import { useCallback, useEffect, useState } from "react";
import { useProjectId } from "hooks/useProjectId";
import LazyImage from "components/elements/LazyImage";
import { XMarkIcon } from "@heroicons/react/24/solid";

const postService = container.resolve(ForumPostService);

type IProps = {
	post: ForumPostResponseResource;
};

export default function ForumHeader(props: IProps) {
	const { projectId } = useProjectId();
	const { image, showImageInFullScreen, closeFullscreen, onImageClick } = usePostImage(projectId, props.post);

	return (
		<div className={classes["root"]}>
			<div className={classes["content"]}>
				<PostAuthorInfo post={props.post} />

				<Typography type="h3" weight="bold">
					{translateISO369(props.post.title)}
				</Typography>

				<Typography type="p" size="large" weight="medium">
					{translateISO369(props.post.description)}
				</Typography>

				{showImageInFullScreen && (
					<div className={classes["fullscreen-image-container"]}>
						<div className={classes["backdrop"]} onClick={closeFullscreen} />
						<LazyImage src={image} alt="" className={classes["image-fullscreen"]} />
						<XMarkIcon className={classes["close-icon"]} onClick={closeFullscreen} />
					</div>
				)}

				{image && (
					<div onClick={onImageClick}>
						<LazyImage src={image} alt="" className={classes["image"]} />
					</div>
				)}
			</div>
		</div>
	);
}

const usePostImage = (projectId: string, post: ForumPostResponseResource) => {
	const [showImageInFullScreen, setShowImageInFullScreen] = useState(false);
	const [image, setImage] = useState<string | null>(null);

	const closeFullscreen = useCallback(() => {
		setShowImageInFullScreen(false);
	}, [setShowImageInFullScreen]);

	const onImageClick = useCallback(() => {
		if (!image) return;
		setShowImageInFullScreen(true);
	}, [image, setShowImageInFullScreen]);

	useEffect(() => {
		if (!post.image) return;
		postService.downloadImage(projectId, post.id).then((response) => {
			if (response) {
				const url = URL.createObjectURL(response);
				setImage(url);
			}
		});
	}, [post.image, post.id, projectId]);

	return { image, showImageInFullScreen, onImageClick, closeFullscreen };
};
